import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
  ensureSharedTableStyles,
  SHARED_TABLE_SCROLL_HEIGHT,
  mergeSharedDataTableCustomStyles,
} from './dataTableStyles';

/**
 * App-wide DataTable: sticky header on vertical scroll, hover + click row highlight.
 * Use in any module (inventory, QC, planning, etc.).
 */
export default function SharedDataTable({
  className = '',
  data = [],
  keyField = 'id',
  getRowKey,
  onRowClicked,
  conditionalRowStyles: conditionalRowStylesProp,
  customStyles: customStylesProp,
  fixedHeader = true,
  fixedHeaderScrollHeight = SHARED_TABLE_SCROLL_HEIGHT,
  highlightOnHover = true,
  striped = true,
  pointerOnHover = true,
  ...rest
}) {
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    ensureSharedTableStyles();
  }, []);

  const resolveRowKey = useCallback((row, index) => {
    if (getRowKey) return getRowKey(row, index);
    const k = row[keyField];
    return k != null ? k : index;
  }, [getRowKey, keyField]);

  const classNames = ['shared-data-table', className].filter(Boolean).join(' ');

  const customStyles = useMemo(
    () => mergeSharedDataTableCustomStyles(customStylesProp),
    [customStylesProp]
  );

  const conditionalRowStyles = useMemo(() => {
    const selectedStyle = {
      when: (row) => {
        const idx = data.indexOf(row);
        const k = resolveRowKey(row, idx);
        return k === selectedKey;
      },
      style: {
        backgroundColor: '#b6d4fe !important',
        fontWeight: 500,
      },
    };
    return [...(conditionalRowStylesProp || []), selectedStyle];
  }, [conditionalRowStylesProp, data, selectedKey, resolveRowKey]);

  const handleRowClicked = (row, event) => {
    const idx = data.indexOf(row);
    const k = resolveRowKey(row, idx);
    setSelectedKey((prev) => (prev === k ? null : k));
    if (onRowClicked) onRowClicked(row, event);
  };

  return (
    <DataTable
      className={classNames}
      data={data}
      keyField={keyField}
      fixedHeader={fixedHeader}
      fixedHeaderScrollHeight={fixedHeaderScrollHeight}
      highlightOnHover={highlightOnHover}
      striped={striped}
      pointerOnHover={pointerOnHover}
      customStyles={customStyles}
      conditionalRowStyles={conditionalRowStyles}
      onRowClicked={handleRowClicked}
      {...rest}
    />
  );
}
